import { notify } from '@tiger/bugsnag';
import { useSWR } from '@tiger/swr';
import { Button, Form } from 'antd';
import { isNumber } from 'lodash';
import React, { useMemo, useRef, useState } from 'react';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import FieldSelect from 'app/components/FieldSelect';
import FormItem from 'app/components/FormItem';
import API from 'app/utils/API';
import BottomBox from 'components/BottomBox';
import CurrencyAmount from 'components/CurrencyAmount';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import Modal, { useAlertModal } from 'components/Modal';
import ScrollTop from 'components/ScrollTop';
import Toast from 'components/Toast';
import AchBankItem from 'components/vendor/ach/AchBankItem';
import AchRemind from 'components/vendor/ach/AchRemind';
import AchSafeVerify from 'components/vendor/ach/AchSafeVerify';
import NoBindAch from 'components/vendor/ach/NoBindAch';
import { PlaidBankCardItem } from 'components/vendor/plaid';
import { UpdatePlaidLocationState } from 'components/vendor/type';
import AddBank from 'modules/Deposit/components/AddBank';
import SwitchContributionWay from 'modules/Deposit/components/SwitchContributionWay';
import TaxLimit from 'modules/Deposit/components/TaxLimit';
import { RecommendDepositEnum } from 'modules/Deposit/type';
import { ReactComponent as IconArrowRight } from 'static/images/icons/icon-arrow-right.svg';
import APP from 'stores/APP';
import security from 'stores/security';
import { formatMoney } from 'utils/format';
import { getCurrencyInfo } from 'utils/util';
import { getTitleByCustomerType } from '../helper';
import EyeSwitch from './EyeSwitch';
import { getBalance, refreshCard } from './request';
import './style.scss';

const supportCurrency = ['USD'];

interface FillProps {
    $formutil: $Formutil;
    isIRA?: boolean;
}

const Fill: React.FC<FillProps> = ({ $formutil, isIRA }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { $invalid, $batchState, $getFirstError, $params } = $formutil;
    const limitNotifiedRef = useRef(false);

    const [currencyList] = useState(() => {
        return supportCurrency.map(currency => {
            const item = getCurrencyInfo(currency);

            return {
                label: item.name,
                icon: item.icon,
                value: currency
            };
        });
    });
    const { open: openAlertModal } = useAlertModal();
    const bankId: number = $params.bank_id;

    const {
        data: bankCardList,
        isLoading: bankCardListLoading,
        error: bankCardListError,
        mutate: bankCardListMutate
    } = useSWR<PlaidBankCardItem[]>(
        isIRA && {
            url: API.TRADE.funding.getCardList(),
            params: {
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId,
                supported_bank: true // 是否支持从bank-card-relation获取银行账户 兼容旧版逻辑
            }
        },
        {
            dataChain: resp =>
                resp.data?.items?.filter(({ status, id_source }) => {
                    /**
                     * plaid银行卡全部展示
                     * 手动认证银行卡展示可用的
                     * */
                    if (id_source === 'Bank') {
                        return status === 'AVAILABLE';
                    }

                    return true;
                })
        }
    );

    const locationState: PlaidBankCardItem | null = useMemo(() => {
        if (!isIRA) {
            return location.state as PlaidBankCardItem | null;
        }

        return bankCardList?.find(item => item.id === bankId) || null;
    }, [isIRA, location.state, bankId, bankCardList]);

    // 获取银行余额（仅 Plaid 关联银行卡需要）
    const {
        error: balanceError,
        data: balance,
        mutate: balanceMutate
    } = useSWR<number | undefined>(
        !!locationState && locationState.id_source === 'Fund' && `deposit.ach.balance.${locationState.account_id}`,
        {
            fetcher: () => {
                const { account_id } = locationState!;

                return getBalance(account_id)
                    .then(res => res?.available || res?.current || 0)
                    .catch(async error => {
                        // 收款人信息不匹配，需要更新银行卡信息
                        if (error.error_code === 13035) {
                            const data = await refreshCard(account_id);

                            const updatePlaidState: UpdatePlaidLocationState = {
                                selectedBank: locationState!,
                                token: data.public_token,
                                source: 'deposit'
                            };

                            navigate('../update-plaid', {
                                state: updatePlaidState,
                                replace: true
                            });

                            return undefined;
                        }

                        throw error;
                    });
            },
            shouldRetryOnError: false,
            revalidateOnFocus: false
        }
    );

    // 获取入金限额（提交校验用）
    const {
        error: limitError,
        data: limitData,
        isLoading: isLimitLoading,
        mutate: limitMutate
    } = useSWR<{
        available_deposit_times: number; // 剩余入金次数
        five_day: number; // 剩余可入金金额
        once_deposit: number; // 一次最大入金金额
    }>(
        {
            url: API.TRADE.funding.availableCash(),
            params: {
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId
            }
        },
        {
            dataChain: resp => resp.data?.deposit,
            revalidateOnFocus: false
        }
    );

    const bankCardArr = useMemo(() => {
        const bankCardArr = bankCardList || [];

        return [
            ...bankCardArr.map(item => ({
                label: `${item.institution_name}${item.account ? `(${item.account.slice(-4)})` : ''}`,
                value: item.id
            })),
            {
                key: __('Add bank account'),
                label: __('Add bank account'),
                value: 'add',
                extraLabel: <AddBank source="deposit" />
            }
        ];
    }, [bankCardList]);

    const handleSubmit = () => {
        const { account_id } = locationState!;

        if ($invalid) {
            $batchState({
                $dirty: true,
                $touched: true
            });

            Toast.show($getFirstError());
        } else {
            const { amount } = $params;
            const { available_deposit_times, five_day, once_deposit } = limitData || {};
            const reason: string[] = [];

            if (isNumber(once_deposit) && once_deposit < amount) {
                reason.push(__('Each ACH deposit cannot exceed $25,000.'));
            }

            if (available_deposit_times === 0) {
                reason.push(__('Each account can initiate at most 4 times of ACH deposit per day.'));
            }

            if (isNumber(five_day) && amount > five_day) {
                reason.push(
                    __('Each account can initiate at most $100,000 ACH deposit in any 5 consecutive business days.')
                );
            }

            if (reason.length > 0) {
                reason.push(__('If you would like to make more deposit ASAP, please wire the funds in.'));

                // 上报 ACH 入金限制弹窗触发原因（仅上报一次）
                if (!limitNotifiedRef.current) {
                    limitNotifiedRef.current = true;

                    notify(new Error('ACH deposit limit reached'), {
                        amount,
                        available_deposit_times,
                        once_deposit,
                        five_day
                    });
                }

                openAlertModal({
                    content: (
                        <>
                            {reason.map(item => (
                                <p className="mb-1" key={item}>
                                    {item}
                                </p>
                            ))}
                        </>
                    ),
                    okText: __('Confirm')
                });

                return;
            }

            const tax = {
                year: $params.tax_year,
                limit: $params.tax_limit,
                // 因为 ach 方式入金是立刻成功的，在结果页会立刻获取到更新之后的剩余限额，所以这里直接传 0，在结果页不需要进行额度计算
                cost: 0
            };

            if (security.optStatus) {
                Modal.open({
                    component: props => <AchSafeVerify {...props} process="fund" />,
                    maskClosable: true,
                    wrapClassName: 'safe-verify-modal',
                    closable: true
                })
                    .result.then(data => {
                        const submitParams = {
                            account_id,
                            amount,
                            currency: 'USD',
                            otp_code: data.otp_code,
                            verify_token: data.verify_token
                        };

                        navigate('../confirm', {
                            state: {
                                params: submitParams,
                                selectedBank: locationState,
                                tax
                            }
                        });
                    })
                    .catch(() => {});
            } else {
                navigate('../confirm', {
                    state: {
                        params: { amount, account_id, currency: 'USD' },
                        selectedBank: locationState,
                        tax
                    }
                });
            }
        }
    };

    const render = () => {
        if (
            (balanceError && balance === undefined) ||
            (limitError && !limitData) ||
            (bankCardListError && !bankCardList)
        ) {
            // plaid没有匹配到当前银行卡
            if (balanceError?.error_code === 403052) {
                return <NoBindAch source="deposit" onRetry={() => balanceMutate()} />;
            }

            return (
                <ErrorBox
                    error={balanceError || limitError || bankCardListError}
                    onClick={() => {
                        balanceError && balanceMutate();
                        limitError && limitMutate();
                        bankCardListError && bankCardListMutate();
                    }}
                />
            );
        }

        if (bankCardListLoading) {
            return <Loading />;
        }

        return (
            <div>
                {security.isAccountOpened && (
                    <Link
                        className="recurring-link"
                        to="/profile/deposit/ach/recurring"
                        state={{ bankInfo: locationState, amount: $params.amount }}>
                        {isIRA ? __('Recurring Contribution') : __('Recurring Deposit')}
                        <IconArrowRight fontSize={14} />
                    </Link>
                )}
                <Form layout="vertical" onFinish={handleSubmit}>
                    <TaxLimit />
                    {!isIRA && (
                        <div className="form-item-space">
                            <p className="label">{__('Transfer Funds From')}</p>
                            <AchBankItem info={locationState!} withBorder />
                        </div>
                    )}
                    {typeof balance !== 'undefined' && (
                        <EyeSwitch
                            className="form-item-space"
                            label={__('Balance:')}
                            value={formatMoney(balance, '$')}
                        />
                    )}
                    <div className="form-item-space">
                        <p className="label">{__('Amount')}</p>
                        <CurrencyAmount
                            currencyList={currencyList}
                            inputConfig={{
                                name: 'amount',
                                placeholder: '0.00',
                                checker: value => {
                                    if (!value) {
                                        return __('Please enter an amount');
                                    }

                                    if (value <= 0) {
                                        return __('The amount you entered is invalid');
                                    }

                                    if (typeof balance !== 'undefined' && value > balance) {
                                        return isIRA
                                            ? __(
                                                  'Please lower your contribute amount or try with a different bank account'
                                              )
                                            : __(
                                                  'Please lower your deposit amount or try with a different bank account'
                                              );
                                    }

                                    return true;
                                }
                            }}
                            selectConfig={{
                                disabled: currencyList.length <= 1,
                                name: 'currency',
                                $defaultValue: 'USD',
                                showIcon: false
                            }}
                        />
                    </div>
                    {isIRA && (
                        <FormItem
                            name="bank_id"
                            required
                            label={__('Transfer from')}
                            $defaultValue={isIRA ? bankCardList![0]?.id : undefined}>
                            {bankCardList!.length > 0 ? (
                                <FieldSelect renderInComponent data={bankCardArr} />
                            ) : (
                                <Button
                                    type="link"
                                    className="pl-0"
                                    onClick={() =>
                                        navigate('/profile/bankcards/auth/select-way', {
                                            state: { source: 'deposit' },
                                            replace: true
                                        })
                                    }>
                                    {__('Add bank account')}
                                </Button>
                            )}
                        </FormItem>
                    )}
                    <AchRemind />
                    <BottomBox className="btn-box">
                        <Button
                            type="primary"
                            block
                            className="btn-important"
                            htmlType="submit"
                            loading={isLimitLoading}>
                            {__('Continue')}
                        </Button>
                        <div className="text-center mt-2">
                            <Link to="../../ach">{__('Bank profiles')}</Link>
                        </div>
                        {APP.isTiger && (
                            <p className="policy-text">TradeUP Securities, Inc. is a member of FINRA and SIPC.</p>
                        )}
                    </BottomBox>
                </Form>
            </div>
        );
    };

    // 非 IRA 模式进入页面时应该带有 location.state
    if (!isIRA && !locationState) {
        return <Navigate to="/profile/deposit/ach" replace />;
    }

    return (
        <div className="deposit-ach-fill-root">
            <ScrollTop />
            <h3 className="main-title">
                <span>{getTitleByCustomerType()}</span>
                <SwitchContributionWay currentWay={RecommendDepositEnum.ACH} />
            </h3>
            <div>{render()}</div>
        </div>
    );
};

export default withForm(Fill);
